import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';

export interface Product {
  _id: string;
  refNo?: string;
  name: string;
  code: string;
  description?: string;
  minPrice?: number;
  maxPrice?: number;
  price?: string;
  currency?: string;
  reservations?: number;
  availableTerritories: Array<{
    _id: string;
    name: string;
    region?: string;
  }>;
  subscriberId: {
    _id: string;
    companyName: string;
  };
  isActive: boolean;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  availableTerritoryCount?: number;
  totalActiveTerritoryCount?: number;
}

export interface PagedProductsRequest {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 1 | -1;
  filters?: {
    searchText?: string;
    isActive?: boolean;
    isArchived?: boolean;
    selectedSubscriberId?: string;
    currency?: string;
  };
}

export interface PagedProductsResponse {
  message: string;
  data: Product[];
  metadata: Array<{
    total: number;
    page: number;
  }>;
}

export interface CreateProductRequest {
  name: string;
  code?: string; // Made optional since backend will auto-generate if not provided
  description?: string;
  minPrice?: number;
  maxPrice?: number;
  price?: string;
  currency?: string;
  availableTerritories?: string[];
}

export interface UpdateProductRequest {
  name?: string;
  code?: string;
  description?: string;
  minPrice?: number;
  maxPrice?: number;
  price?: string;
  currency?: string;
  reservations?: number;
  availableTerritories?: string[];
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  constructor(private http: HttpClient) {}

  // Get paged products with advanced filtering
  getPagedProducts(params: PagedProductsRequest): Observable<PagedProductsResponse> {
    return this.http.post<{message: string, data: Product[], metadata: Array<{total: number, page: number}>}>(`${environment.apiUrl}/products/paged`, params).pipe(
      map(response => ({
        message: response.message,
        data: response.data || [],
        metadata: response.metadata || []
      }))
    );
  }

  // Get product by ID
  getProductById(id: string): Observable<Product> {
    return this.http.get<{message: string, data: Product}>(`${environment.apiUrl}/products/${id}`).pipe(
      map(response => response.data)
    );
  }

  // Create product
  createProduct(product: CreateProductRequest): Observable<Product> {
    return this.http.post<{message: string, data: Product}>(`${environment.apiUrl}/products`, product).pipe(
      map(response => response.data)
    );
  }

  // Update product
  updateProduct(id: string, product: UpdateProductRequest): Observable<Product> {
    return this.http.put<{message: string, data: Product}>(`${environment.apiUrl}/products/${id}`, product).pipe(
      map(response => response.data)
    );
  }

  // Archive product
  archiveProduct(id: string): Observable<Product> {
    return this.http.patch<{message: string, data: Product}>(`${environment.apiUrl}/products/${id}/archive`, {}).pipe(
      map(response => response.data)
    );
  }

  // Restore product
  restoreProduct(id: string): Observable<Product> {
    return this.http.patch<{message: string, data: Product}>(`${environment.apiUrl}/products/${id}/restore`, {}).pipe(
      map(response => response.data)
    );
  }



  // Get products by subscriber ID
  getProductsBySubscriberId(subscriberId: string, isActive?: boolean, isArchived: boolean = false): Observable<Product[]> {
    let url = `${environment.apiUrl}/products/subscriber/${subscriberId}`;
    const params: string[] = [];
    
    if (isActive !== undefined) {
      params.push(`isActive=${isActive}`);
    }
    if (isArchived !== undefined) {
      params.push(`isArchived=${isArchived}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    return this.http.get<{message: string, data: Product[]}>(url).pipe(
      map(response => response.data || [])
    );
  }

  // Get all products
  getAllProducts(): Observable<Product[]> {
    return this.http.get<{message: string, data: Product[]}>(`${environment.apiUrl}/products`).pipe(
      map(response => response.data || [])
    );
  }
}
